import { useState, useEffect, useRef, useCallback } from "react";
import { getDevices, getDeviceLive } from "../services/api";
import { zoneSummary } from "../utils/heartRateZones";

const HR_POLL_INTERVAL_MS = 3000;

/**
 * Hook that polls a linked ESP32 device for heart rate data during workouts.
 * Collects HR samples and computes running aggregates (avg, max, min).
 *
 * @param {boolean} active - Whether to actively poll (true when workout is running)
 * @returns {{ currentBpm: number|null, hrSummary: object|null, isTracking: boolean }}
 */
export default function useDeviceHr(active, hrBasis = null) {
  // Zones (#94) use the basis at the time of each summary.
  const hrBasisRef = useRef(hrBasis);
  hrBasisRef.current = hrBasis;
  const [currentBpm, setCurrentBpm] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const samplesRef = useRef([]);
  const summaryRef = useRef(null);
  const intervalRef = useRef(null);

  // Resolve the first linked device on activation
  useEffect(() => {
    if (!active) {
      setDeviceId(null);
      return;
    }

    let cancelled = false;
    getDevices()
      .then((devices) => {
        if (!cancelled && devices.length > 0) {
          setDeviceId(devices[0].id);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [active]);

  // Compute summary from collected samples
  const computeSummary = useCallback(() => {
    const samples = samplesRef.current;
    if (samples.length === 0) return null;

    const bpms = samples.map((s) => s.bpm);
    const sum = bpms.reduce((a, b) => a + b, 0);

    const zones = zoneSummary(bpms, hrBasisRef.current, HR_POLL_INTERVAL_MS / 1000);
    return {
      avg_bpm: Math.round(sum / bpms.length),
      max_bpm: Math.max(...bpms),
      min_bpm: Math.min(...bpms),
      samples: bpms.length,
      // Each poll stands for the 3 s until the next one.
      ...(zones ? { zones } : {}),
    };
  }, []);

  // Poll device heartbeat
  useEffect(() => {
    if (!active || !deviceId) {
      clearInterval(intervalRef.current);
      setIsTracking(false);
      return;
    }

    setIsTracking(true);

    const poll = async () => {
      try {
        const data = await getDeviceLive(deviceId);
        if (data?.hr_bpm != null) {
          setCurrentBpm(data.hr_bpm);
          samplesRef.current.push({
            bpm: data.hr_bpm,
            timestamp: Date.now(),
          });
          summaryRef.current = computeSummary();
        }
      } catch {
        // Device offline — skip this tick
      }
    };

    poll();
    intervalRef.current = setInterval(poll, HR_POLL_INTERVAL_MS);

    return () => clearInterval(intervalRef.current);
  }, [active, deviceId, computeSummary]);

  // Reset samples when deactivated
  useEffect(() => {
    if (!active) {
      samplesRef.current = [];
      summaryRef.current = null;
      setCurrentBpm(null);
    }
  }, [active]);

  return {
    currentBpm,
    /** Call this to get the final HR summary when the workout finishes */
    getHrSummary: computeSummary,
    hrSummaryRef: summaryRef,
    isTracking,
  };
}
